import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && localStorage.getItem('token')) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

export default api

// --- Auth ---
export const login = (email: string, password: string) =>
  api.post('/auth/login', new URLSearchParams({ username: email, password }))

export const register = (data: { company_name: string; email: string; password: string; name: string }, queryString = '') =>
  api.post(`/auth/register${queryString}`, data)

export const sendContactMessage = (data: { name: string; email: string; phone?: string; message: string }) =>
  api.post('/contact/', data)

export const getMe = () => api.get('/auth/me')

// --- Ingredients ---
export const getIngredients = (q?: string) =>
  api.get('/ingredients/', { params: q ? { q } : {} })
export const getCompositionOverrides = () => api.get('/ingredients/composition-overrides')
export const setCompositionOverride = (id: number, composition: string) =>
  api.put(`/ingredients/${id}/composition`, { composition })

export const createIngredient = (data: object) => api.post('/ingredients/', data)
export const updateIngredient = (id: number, data: object) => api.put(`/ingredients/${id}`, data)
export const deleteIngredient = (id: number) => api.delete(`/ingredients/${id}`)

// --- Recipes ---
export const getRecipes = () => api.get('/recipes/')
export const getRecipe = (id: number) => api.get(`/recipes/${id}`)
export const createRecipe = (data: object) => api.post('/recipes/', data)
export const updateRecipe = (id: number, data: object) => api.put(`/recipes/${id}`, data)
export const deleteRecipe = (id: number) => api.delete(`/recipes/${id}`)

export const aiImportRecipe = (params: { text?: string; file?: File }) => {
  const fd = new FormData()
  if (params.text) fd.append('text', params.text)
  if (params.file) fd.append('file', params.file)
  return api.post('/recipes/ai-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// --- Documents ---
export const downloadReceptKarta = (recipeId: number) =>
  api.post(`/documents/recept-karta/${recipeId}`, {}, { responseType: 'blob' })

export const downloadGyartmanylap = (recipeId: number) =>
  api.post(`/documents/gyartmanylap/${recipeId}`, {}, { responseType: 'blob' })

export const downloadHaccp = (recipeId: number) =>
  api.post(`/documents/haccp/${recipeId}`, {}, { responseType: 'blob' })

export const downloadLabel = (recipeId: number, params: object) =>
  api.post(`/documents/label/${recipeId}`, params, { responseType: 'blob' })

export const downloadSzarmazasi = (recipeId: number, params: { production_date?: string; expiry_date?: string }) =>
  api.post(`/documents/szarmazasi/${recipeId}`, params, { responseType: 'blob' })

export const getCompany = () => api.get('/documents/company')
export const updateCompany = (data: object) => api.put('/documents/company', data)
export const uploadLogo = (file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  return api.post('/documents/company/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const deleteLogo = () => api.delete('/documents/company/logo')

// --- Subscription ---
export const getSubscription = () => api.get('/subscription/')
export const getPlans = () => api.get('/subscription/plans')
export const getAddons = () => api.get('/subscription/addons')
export const createCheckout = (price_key: string) => api.post('/stripe/checkout', { price_key })
export const createPortalSession = () => api.post('/stripe/portal')
export const getStripeSubscription = () => api.get('/stripe/current')
export const getChangePreview = (price_key: string) => api.get('/stripe/change-preview', { params: { price_key } })
export const changePlan = (price_key: string, proration_date?: number) => api.post('/stripe/change-plan', { price_key, proration_date })
export const cancelPendingChange = () => api.post('/stripe/cancel-pending-change')
export const getReferral = () => api.get('/stripe/referral')
export const getCampaign = () => api.get('/stripe/campaign')

// --- Ingredients (Békás) ---
export const getIngredientsBekas = (q?: string) =>
  api.get('/ingredients/bekas', { params: q ? { q } : {} })
export const getIngredientsBekasPreview = () => api.get('/ingredients/preview-bekas')

// --- HACCP logs ---
export const getHaccpLogs = (params?: object) => api.get('/haccp/logs', { params })
export const createHaccpLog = (data: object) => api.post('/haccp/logs', data)
export const updateHaccpLog = (id: number, data: object) => api.put(`/haccp/logs/${id}`, data)
export const deleteHaccpLog = (id: number) => api.delete(`/haccp/logs/${id}`)

// --- Hűtő/fagyasztó eszközök ---
export const getFridgeDevices = () => api.get('/haccp/devices')
export const createFridgeDevice = (data: object) => api.post('/haccp/devices', data)
export const updateFridgeDevice = (id: number, data: object) => api.put(`/haccp/devices/${id}`, data)
export const deleteFridgeDevice = (id: number) => api.delete(`/haccp/devices/${id}`)

// --- Hűtő/fagyasztó napló ---
export const getFridgeLogs = (params?: object) => api.get('/haccp/fridge', { params })
export const createFridgeLog = (data: object) => api.post('/haccp/fridge', data)
export const updateFridgeLog = (id: number, data: object) => api.put(`/haccp/fridge/${id}`, data)
export const deleteFridgeLog = (id: number) => api.delete(`/haccp/fridge/${id}`)

// --- Takarítási napló ---
export const getCleaningLogs = (params?: object) => api.get('/haccp/cleaning', { params })
export const createCleaningLog = (data: object) => api.post('/haccp/cleaning', data)
export const updateCleaningLog = (id: number, data: object) => api.put(`/haccp/cleaning/${id}`, data)
export const deleteCleaningLog = (id: number) => api.delete(`/haccp/cleaning/${id}`)

// --- Termelési napló ---
export const getProductionLogs = (params?: object) => api.get('/haccp/production', { params })
export const createProductionLog = (data: object) => api.post('/haccp/production', data)
export const updateProductionLog = (id: number, data: object) => api.put(`/haccp/production/${id}`, data)
export const deleteProductionLog = (id: number) => api.delete(`/haccp/production/${id}`)
export const getDisplayLogs = (params?: object) => api.get('/haccp/display', { params })
// Konyhai címke (Brother QL 62 mm): termelési napló / félkész készlet / pultnapló bejegyzésből
export const getKitchenLabel = (source: 'production' | 'semifinished' | 'display', id: number) =>
  api.get(`/documents/kitchen-label/${source}/${id}`, { responseType: 'blob' })
export const createDisplayLog = (data: object) => api.post('/haccp/display', data)
export const removeDisplayLog = (id: number, data: object) => api.post(`/haccp/display/${id}/remove`, data)
export const undoRemoveDisplayLog = (id: number) => api.post(`/haccp/display/${id}/undo-remove`)
export const deleteDisplayLog = (id: number) => api.delete(`/haccp/display/${id}`)

// --- Félkész termék készlet ---
export const getSemifinished = (activeOnly = true) => api.get('/haccp/semifinished', { params: { active_only: activeOnly } })
export const createSemifinished = (data: object) => api.post('/haccp/semifinished', data)
export const patchSemifinished = (id: number, params: object) => api.patch(`/haccp/semifinished/${id}`, null, { params })
export const deleteSemifinished = (id: number) => api.delete(`/haccp/semifinished/${id}`)

// --- Nyomonkövetés (alapanyag-tétel + QR) ---
export const getIngredientBatches = (ingredientId?: number) =>
  api.get('/traceability/batches', { params: ingredientId ? { ingredient_id: ingredientId } : {} })
export const createIngredientBatch = (data: object) => api.post('/traceability/batches', data)
export const deleteIngredientBatch = (id: number) => api.delete(`/traceability/batches/${id}`)

export const getTraceabilityRecords = (recipeId?: number) =>
  api.get('/traceability/records', { params: recipeId ? { recipe_id: recipeId } : {} })
export const createTraceabilityRecord = (data: { recipe_id: number; production_date: string }) =>
  api.post('/traceability/records', data)
export const getPublicTraceability = (token: string) =>
  api.get(`/traceability/public/${token}`)

export const uploadBatchPhoto = (batchId: number, file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  return api.post(`/traceability/batches/${batchId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const getBatchPhoto = (batchId: number) =>
  api.get(`/traceability/batches/${batchId}/photo`, { responseType: 'blob' })

// --- Munkatársak ---
export const getStaff = () => api.get('/staff/')
export const createStaff = (data: object) => api.post('/staff/', data)
export const updateStaff = (id: number, data: object) => api.put(`/staff/${id}`, data)
export const deleteStaff = (id: number) => api.delete(`/staff/${id}`)

// --- Helpers ---
export function triggerPdfDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => window.URL.revokeObjectURL(url), 100)
}

export function openPdfPreview(blob: Blob) {
  const url = window.URL.createObjectURL(blob)
  window.open(url, '_blank')
}

export function printPdf(blob: Blob) {
  const url = window.URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = 'none'
  iframe.src = url
  document.body.appendChild(iframe)
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } catch {
      // ha a böngésző letiltja az iframe-en belüli nyomtatást, essünk vissza az előnézetre
      window.open(url, '_blank')
    }
  }
  // az iframe-et és az object URL-t egy idő után eltávolítjuk, hogy ne folyjon a memória
  setTimeout(() => {
    document.body.removeChild(iframe)
    window.URL.revokeObjectURL(url)
  }, 60000)
}

// Brother 62 mm-es tekercses címke — három út, ebben a sorrendben:
//  1. ezen a gépen fut a címkenyomtató segéd → közvetlenül neki (pontos hossz, nincs nyomtatási ablak);
//  2. telefon / tablet / másik gép: a felhős nyomtatási sorba küldjük, és az üzlet párosított gépén futó
//     segéd nyomtatja ki (ha épp elérhető);
//  3. különben a böngésző nyomtatási ablaka (ott a hossz a választott papírmérettől függ).
export const LABEL_HELPER_URL = 'http://127.0.0.1:17865'
export const PRINT_QUEUE_BASE = 'https://cukraszdoc.hu/documents/print'   // a segéd ezt figyeli párosítás után

export const pairPrintBridge = () => api.post('/documents/print/bridges')
export const getPrintBridges = () => api.get('/documents/print/bridges')
export const deletePrintBridge = (id: number) => api.delete(`/documents/print/bridges/${id}`)
const createPrintJob = (blob: Blob) =>
  api.post('/documents/print/jobs', blob, { headers: { 'Content-Type': 'application/pdf' } })

export async function labelHelperPair(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${LABEL_HELPER_URL}/pair`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal: AbortSignal.timeout(3000),
    })
    return res.ok
  } catch {
    return false
  }
}

export async function labelHelperStatus(): Promise<{ ok: boolean; printer: string | null; paired?: string[] } | null> {
  try {
    const res = await fetch(`${LABEL_HELPER_URL}/status`, { signal: AbortSignal.timeout(1500) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

export async function printRollLabel(blob: Blob): Promise<'helper' | 'cloud' | 'browser'> {
  let res: Response
  try {
    res = await fetch(`${LABEL_HELPER_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/pdf' },
      body: blob,
      signal: AbortSignal.timeout(15000),
    })
  } catch {
    // ezen az eszközön nem fut a segéd → felhős sor (az üzlet párosított gépe nyomtatja), különben böngésző
    try {
      await createPrintJob(blob)
      return 'cloud'
    } catch {
      printPdf(blob)
      return 'browser'
    }
  }
  const body = await res.json().catch(() => ({}))
  if (res.ok && body.ok) return 'helper'
  if (res.status === 500) throw new Error(`Címkenyomtató: ${body.error || 'ismeretlen hiba'}`)
  printPdf(blob)
  return 'browser'
}
